import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import ProspectsStatsBloc from '../components/ProspectsStatsBloc';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Layers, Loader2, Calendar, X } from 'lucide-react';
import { useStatsBlockGuard } from '../hooks/useStatsBlockGuard';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PALETTE = [
    '#1B365D', '#C9A962', '#10B981', '#0EA5E9', '#8B5CF6', '#EF4444',
    '#F59E0B', '#14B8A6', '#EC4899', '#6366F1', '#84CC16', '#64748B',
];

const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const j = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${j}`;
};

function StackedSourceStatut() {
    const [datePreset, setDatePreset] = useState('all');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const applyDatePreset = (p) => {
        setDatePreset(p);
        const today = new Date(); const iso = toISO(today);
        if (p === 'all') { setDateFrom(''); setDateTo(''); return; }
        if (p === 'today') { setDateFrom(iso); setDateTo(iso); return; }
        if (p === '7d') { const d = new Date(); d.setDate(d.getDate() - 6); setDateFrom(toISO(d)); setDateTo(iso); return; }
        if (p === '30d') { const d = new Date(); d.setDate(d.getDate() - 29); setDateFrom(toISO(d)); setDateTo(iso); return; }
    };
    const clearDates = () => { setDatePreset('all'); setDateFrom(''); setDateTo(''); };

    useEffect(() => {
        const params = {};
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        setLoading(true);
        axios.get(`${API_URL}/api/statistiques/prospects`, { params, withCredentials: true })
            .then(r => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [dateFrom, dateTo]);

    const chartRows = data?.cross_source_statut || [];
    const statutKeys = data?.statut_labels || [];

    const colorMap = useMemo(() => {
        const m = {};
        statutKeys.forEach((k, i) => { m[k] = PALETTE[i % PALETTE.length]; });
        return m;
    }, [statutKeys]);

    return (
        <Card className="border-t-4 border-t-[#C9A962] shadow-sm" data-testid="stats-stacked-bloc">
            <CardContent className="p-5 sm:p-6 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2.5">
                        <div className="h-10 w-10 rounded-full bg-[#C9A962]/15 flex items-center justify-center">
                            <Layers className="h-5 w-5 text-[#C9A962]" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#1B365D] text-base sm:text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                Diagramme empilé — Source × Statut
                            </h3>
                            <p className="text-xs text-gray-500">Pour chaque source, répartition par statut</p>
                        </div>
                    </div>
                </div>

                {/* Period filter */}
                <div className="flex items-center gap-2 flex-wrap">
                    <Calendar className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                    <div className="flex flex-wrap gap-0.5 bg-gray-100 p-0.5 rounded-md">
                        {[
                            { v: 'all', label: 'Tout' },
                            { v: 'today', label: "Aujourd'hui" },
                            { v: '7d', label: '7 jours' },
                            { v: '30d', label: '30 jours' },
                            { v: 'custom', label: 'Personnalisé' },
                        ].map(p => (
                            <button key={p.v} onClick={() => applyDatePreset(p.v)}
                                className={`px-2.5 py-1 rounded text-[11px] font-medium transition-colors ${datePreset === p.v ? 'bg-white text-[#1B365D] shadow-sm' : 'text-gray-600 hover:text-[#1B365D]'}`}
                                data-testid={`stacked-preset-${p.v}`}
                            >{p.label}</button>
                        ))}
                    </div>
                    {datePreset === 'custom' && (
                        <div className="flex items-center gap-1.5">
                            <input type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="stacked-date-from" />
                            <span className="text-gray-400 text-[10px]">→</span>
                            <input type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setDatePreset('custom'); }} className="border border-gray-200 rounded h-7 px-1.5 text-[11px] w-[125px]" data-testid="stacked-date-to" />
                        </div>
                    )}
                    {(dateFrom || dateTo) && (
                        <button onClick={clearDates} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[11px] text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                            <X className="w-3 h-3" /> Effacer
                        </button>
                    )}
                </div>

                {/* Chart */}
                <div className="bg-gray-50/50 border border-gray-100 rounded-lg p-3" style={{ height: 420 }}>
                    {loading ? (
                        <div className="h-full flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    ) : chartRows.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center text-gray-400 text-sm">
                            <Layers className="w-10 h-10 mb-2 text-gray-300" />
                            Aucune donnée sur la période sélectionnée
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartRows} margin={{ top: 10, right: 10, left: 0, bottom: 70 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={false} />
                                <XAxis
                                    dataKey="source"
                                    tick={{ fontSize: 11, fill: '#475569' }}
                                    interval={0}
                                    angle={-25}
                                    textAnchor="end"
                                />
                                <YAxis tick={{ fontSize: 11, fill: '#475569' }} allowDecimals={false} />
                                <Tooltip
                                    cursor={{ fill: 'rgba(27,54,93,0.05)' }}
                                    contentStyle={{ borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 12 }}
                                />
                                <Legend verticalAlign="bottom" wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                                {statutKeys.map((k) => (
                                    <Bar
                                        key={k}
                                        dataKey={k}
                                        stackId="statut"
                                        fill={colorMap[k]}
                                        radius={[0, 0, 0, 0]}
                                    />
                                ))}
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default function StatistiquesProspects() {
    useStatsBlockGuard('stats_prospects');
    const navigate = useNavigate();
    return (
        <Layout>
            <div className="space-y-6 animate-fadeIn" data-testid="statistiques-prospects-page">
                {/* Header */}
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(-1)}
                            className="h-8 w-8 p-0"
                            data-testid="stats-prospects-back-btn"
                            aria-label="Retour"
                        >
                            <ArrowLeft className="h-4 w-4" />
                        </Button>
                        <div>
                            <h1
                                className="text-2xl sm:text-3xl font-bold text-[#1B365D]"
                                style={{ fontFamily: 'Outfit, sans-serif' }}
                                data-testid="stats-prospects-title"
                            >
                                Prospects — Analyse
                            </h1>
                            <p className="text-gray-500 mt-0.5 text-sm">Répartition détaillée par statut, source et type de visite</p>
                        </div>
                    </div>
                </div>

                {/* Bloc principal (barres/camembert + KPI visites) */}
                <ProspectsStatsBloc />

                {/* Diagramme empilé Source × Statut */}
                <StackedSourceStatut />
            </div>
        </Layout>
    );
}
